/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type DefaultOptions, QueryClient } from '@tanstack/react-query';
import { parseRetryConfigString } from './retry/parseRetryConfig';
import environmentConfig from '@/config/environment';
import { isServer } from '@/utils/isServer';

const retryDefaults = parseRetryConfigString(environmentConfig.RETRY_DEFAULT);

export const defaultOptions: DefaultOptions = {
  queries: {
    throwOnError: false,
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000, // global default to 30 seconds
    ...retryDefaults,
  },
};

export const botOptions: DefaultOptions = {
  queries: {
    enabled: false,
    throwOnError: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  },
};

let clientSideQueryClient: QueryClient;

// We create a separate instance of QueryClient for every getQueryClient call on server so
// that it doesn't leak between the pages and users. We _could_ create a
// singleton, but that would require the cacheKey to have some user identifier,
// which we currently don't have.
export const createQueryClient = ({ isBot }: { isBot: boolean }) =>
  new QueryClient({ defaultOptions: isBot ? botOptions : defaultOptions });

// We create singleton instance of QueryClient so that we do not create duplicates on the client
// and have access to the same cache everywhere.
export const getClientSideQueryClient = ({ isBot }: { isBot: boolean }) => {
  if (isServer) {
    throw new Error('This function should only be called client side.');
  }

  if (!clientSideQueryClient) {
    clientSideQueryClient = createQueryClient({ isBot });
  }

  return clientSideQueryClient;
};
